<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useRemindersStore } from 'dashboard/stores/reminders';
import { useNotificationsHub } from 'dashboard/composables/useNotificationsHub';

import Button from 'dashboard/components-next/button/Button.vue';
import ReminderCreateDialog from 'dashboard/components-next/Reminders/ReminderCreateDialog.vue';

const store = useStore();
const remindersStore = useRemindersStore();
const { logActivity } = useNotificationsHub();
const { t, locale } = useI18n();

const dialogRef = ref(null);

const reminders = computed(() => remindersStore.list);

// Reminders that have already fired ("triggered"), eligible to be cleared.
const triggeredCount = computed(
  () => reminders.value.filter(r => r.status === 'fired').length
);

const STATUS_CLASS = {
  pending: 'text-n-amber-11 bg-n-amber-3',
  fired: 'text-n-teal-11 bg-n-teal-3',
  done: 'text-n-slate-11 bg-n-alpha-2',
};
const STATUS_LABEL_KEY = {
  pending: 'REMINDERS.STATUS.PENDING',
  fired: 'REMINDERS.STATUS.FIRED',
  done: 'REMINDERS.STATUS.DONE',
};
const statusClass = status => STATUS_CLASS[status] || STATUS_CLASS.pending;
const statusLabel = status =>
  t(STATUS_LABEL_KEY[status] || STATUS_LABEL_KEY.pending);

const formatTrigger = triggerAt => {
  if (!triggerAt) return '';
  const date = new Date(triggerAt);
  return date.toLocaleString(locale.value.replace('_', '-'), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isOverdue = reminder =>
  reminder.status === 'pending' &&
  reminder.triggerAt &&
  new Date(reminder.triggerAt).getTime() < Date.now();

const openCreate = () => dialogRef.value?.openForCreate();
const openEdit = reminder => dialogRef.value?.openForEdit(reminder);

const onSave = payload => {
  if (payload.id) {
    const { id, ...patch } = payload;
    remindersStore.update(id, { ...patch, status: 'pending', firedAt: null });
    useAlert(t('REMINDERS.ALERTS.UPDATED'));
  } else {
    const { id, ...rest } = payload;
    remindersStore.create(rest);
    logActivity({
      category: 'reminder',
      action: 'scheduled',
      title: t('REMINDERS.ACTIVITY.SCHEDULED_TITLE', { title: rest.title }),
      body: t('REMINDERS.ACTIVITY.SCHEDULED_BODY', {
        datetime: formatTrigger(rest.triggerAt),
      }),
      targetUserIds: rest.agentIds || [],
      meta: { triggerAt: rest.triggerAt },
    });
    useAlert(t('REMINDERS.ALERTS.CREATED', { title: rest.title }));
  }
  dialogRef.value?.onSuccess();
};

const onRemove = id => {
  remindersStore.remove(id);
  dialogRef.value?.onSuccess();
  useAlert(t('REMINDERS.ALERTS.DELETED'));
};

const toggleDone = reminder => remindersStore.toggleDone(reminder.id);
const removeReminder = reminder => remindersStore.remove(reminder.id);

const clearTriggered = async () => {
  if (!triggeredCount.value) {
    useAlert(t('REMINDERS.ALERTS.CLEAR_NONE'));
    return;
  }
  const count = await remindersStore.clearTriggered();
  useAlert(t('REMINDERS.ALERTS.CLEARED', { n: count }));
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('labels/get');
  remindersStore.fetch();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <div
      class="flex items-center justify-between gap-4 px-6 py-4 border-b border-n-weak flex-shrink-0"
    >
      <div class="flex flex-col">
        <h1 class="text-lg font-semibold text-n-slate-12 m-0">
          {{ t('REMINDERS.HEADER.TITLE') }}
        </h1>
        <span class="text-sm text-n-slate-11">
          {{ t('REMINDERS.HEADER.SUBTITLE') }}
        </span>
      </div>
      <div class="flex items-center gap-2 flex-shrink-0">
        <Button
          v-if="triggeredCount"
          :label="t('REMINDERS.HEADER.CLEAR_TRIGGERED')"
          variant="outline"
          color="slate"
          size="sm"
          icon="i-lucide-check-check"
          @click="clearTriggered"
        />
        <Button
          :label="t('REMINDERS.HEADER.NEW')"
          color="blue"
          size="sm"
          icon="i-lucide-plus"
          @click="openCreate"
        />
      </div>
    </div>

    <div class="flex-1 overflow-y-auto p-6">
      <div
        v-if="!reminders.length"
        class="flex flex-col items-center justify-center gap-3 h-full"
      >
        <span class="i-lucide-bell size-10 text-n-slate-9" />
        <span class="text-base text-n-slate-11">
          {{ t('REMINDERS.EMPTY.TITLE') }}
        </span>
        <Button
          :label="t('REMINDERS.EMPTY.CREATE_FIRST')"
          variant="outline"
          color="slate"
          size="sm"
          icon="i-lucide-plus"
          @click="openCreate"
        />
      </div>

      <div v-else class="flex flex-col gap-2">
        <div
          v-for="reminder in reminders"
          :key="reminder.id"
          class="flex items-start gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1 hover:bg-n-alpha-1 transition-colors cursor-pointer"
          @click="openEdit(reminder)"
        >
          <span
            class="grid flex-shrink-0 mt-0.5 size-9 rounded-lg place-content-center bg-n-alpha-2"
          >
            <span
              class="i-lucide-bell size-4"
              :style="{ color: reminder.color || undefined }"
              :class="reminder.color ? '' : 'text-n-amber-11'"
            />
          </span>

          <div class="flex flex-col flex-1 min-w-0 gap-1">
            <div class="flex items-center gap-2 flex-wrap">
              <span
                v-if="reminder.publicId"
                class="text-[11px] font-mono px-1.5 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-10"
              >
                {{ reminder.publicId }}
              </span>
              <span
                class="text-sm font-medium text-n-slate-12"
                :class="{ 'line-through text-n-slate-10': reminder.status === 'done' }"
              >
                {{ reminder.title }}
              </span>
              <span
                class="text-[11px] px-1.5 py-0.5 rounded-md font-medium"
                :class="statusClass(reminder.status)"
              >
                {{ statusLabel(reminder.status) }}
              </span>
              <span
                v-if="isOverdue(reminder)"
                class="text-[11px] px-1.5 py-0.5 rounded-md font-medium text-n-ruby-11 bg-n-ruby-3"
              >
                {{ t('REMINDERS.BADGE.OVERDUE') }}
              </span>
              <span
                v-if="reminder.sourceEventId"
                class="inline-flex items-center gap-1 text-[11px] text-n-slate-10"
              >
                <span class="i-lucide-calendar size-3" />
                {{ t('REMINDERS.BADGE.FROM_EVENT') }}
              </span>
            </div>

            <span
              v-if="reminder.description"
              class="text-sm text-n-slate-11 line-clamp-2"
            >
              {{ reminder.description }}
            </span>

            <div
              class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-n-slate-10"
            >
              <span class="inline-flex items-center gap-1">
                <span class="i-lucide-clock size-3" />
                {{ formatTrigger(reminder.triggerAt) }}
              </span>
              <span
                v-if="reminder.agents?.length"
                class="inline-flex items-center gap-1"
              >
                <span class="i-lucide-users size-3" />
                {{ reminder.agents.map(a => a.name).join(', ') }}
              </span>
              <span
                v-if="reminder.contacts?.length"
                class="inline-flex items-center gap-1"
              >
                <span class="i-lucide-contact size-3" />
                {{ reminder.contacts.map(c => c.name).join(', ') }}
              </span>
            </div>

            <div
              v-if="reminder.labels?.length"
              class="flex flex-wrap items-center gap-1.5 mt-1"
            >
              <span
                v-for="label in reminder.labels"
                :key="label"
                class="text-[11px] px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11"
              >
                {{ label }}
              </span>
            </div>
          </div>

          <div class="flex items-center gap-1 flex-shrink-0">
            <Button
              v-tooltip="
                reminder.status === 'done'
                  ? t('REMINDERS.ACTIONS.REOPEN')
                  : t('REMINDERS.ACTIONS.COMPLETE')
              "
              variant="ghost"
              color="slate"
              size="xs"
              :icon="reminder.status === 'done' ? 'i-lucide-rotate-ccw' : 'i-lucide-check'"
              @click.stop="toggleDone(reminder)"
            />
            <Button
              v-tooltip="t('REMINDERS.ACTIONS.DELETE')"
              variant="ghost"
              color="ruby"
              size="xs"
              icon="i-lucide-trash-2"
              @click.stop="removeReminder(reminder)"
            />
          </div>
        </div>
      </div>
    </div>

    <ReminderCreateDialog ref="dialogRef" @save="onSave" @remove="onRemove" />
  </div>
</template>
